<script setup lang="ts">
import { object, string, type InferType } from 'yup'
import type { FormSubmitEvent } from '#ui/types'

const { locales, setLocale, locale, t } = useI18n()

const toast = useToast()

const auth_store = useAuthStore()

const { is_auth_modal_visible } = storeToRefs(auth_store)

const login_schema = object({
	email: string().email(t('enter_corr_email')).required(t('required')),
	password: string()
		.min(8, t('symb_lim'))
		.required(t('required'))
})

const is_loading = ref(false)

type LoginSchema = InferType<typeof login_schema>

const login_state = reactive({
	email: undefined,
	password: undefined,
	remind: false
})

async function onLoginSubmit(event: FormSubmitEvent<LoginSchema>) {
	// Do something with event.data
	console.log(event.data)

	is_loading.value = true

	try {
		const response = await auth_store.login(event.data.email, event.data.password)

		if (response.success) {
			auth_store.auth_modal_hide()
		}

	} catch (error) {
		throw error;

	} finally {
		is_loading.value = false
	}
}

const register_schema = object({
	email: string().email('Invalid email').required(t('required')),
	password: string()
		// .min(32, 'Не менее 32 символов')
		.test('is_strong', t('password_message'), (value) => {
			return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{32,}$/.test(value)
		})
		.required(t('required'))
})

type RegisterSchema = InferType<typeof register_schema>

const register_state = reactive({
	email: undefined,
	password: undefined
})

async function onRegisterSubmit(event: FormSubmitEvent<RegisterSchema>) {
	// Do something with event.data
	console.log(event.data)

	is_loading.value = true

	try {

		const response = await auth_store.register(event.data.email, event.data.password)

		if (response.success) {
			auth_store.auth_modal_hide()
		} else {
			toast.add({
				title: t('error'),
				description: response.message,
				color: 'red'
			})
		}

	} catch (error) {
		throw error;

	} finally {
		is_loading.value = false
	}


}

const register_form = ref<any>(null) // или укажите правильный тип для UForm если он доступен
const should_validate = ref(false)
const active_tab = ref(1)

function generate_strong_password() {
    const length = 32
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}|:<>?'
    let password = ''
    for (let i = 0; i < length; i++) {
        const at = Math.floor(Math.random() * charset.length)
        password += charset.charAt(at)
    }
    register_state.password = password
	should_validate.value = !should_validate.value

    // nextTick(async () => {
    //     if (register_form.value?.$validate) {
    //         await register_form.value.$validate()
    //     }
    // })
}

const tabs = [{
	slot: 'login',
	label: t('loging_in')
}, {
	slot: 'register',
	label: t('registring')
}]

onMounted(() => {
	if (localStorage.getItem('last_login')) {
		active_tab.value = 0
	}
})

</script>

<template>

<UModal v-model="is_auth_modal_visible">
	<UCard :ui="{ ring: '', divide: 'divide-y divide-gray-100 dark:divide-gray-800' }">

		<template #header>
			<div class="flex items-center justify-between">
				<h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white">
					{{ $t('authorisation') }}
				</h3>
				<UButton color="gray" variant="ghost" icon="i-heroicons-x-mark-20-solid" class="-my-1" @click="auth_store.auth_modal_hide()" />
			</div>
		</template>

		<div class="p-5">
			<!-- <h3 class="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
				Вход
			</h3> -->
			<UForm :schema="login_schema" :state="login_state" class="space-y-4" @submit="onLoginSubmit">

				<UFormGroup label="Email" name="email">
					<UInput v-model="login_state.email" />
				</UFormGroup>

				<UFormGroup :label="t('password')" name="password">
					<UInput v-model="login_state.password" type="password" />
				</UFormGroup>

				<div class="flex justify-end">
					<UCheckbox :label="$t('remember_device')" v-model="login_state.remind" />
				</div>

				<div class="flex justify-end pt-5">
					<UButton type="submit" size="lg" :loading="is_loading">
						{{ $t('login') }}
					</UButton>
				</div>

			</UForm>
		</div>

		<UTabs :items="tabs" v-bind:default-index="active_tab" v-if="false">
			<template #login>
				<div class="p-5">
					<!-- <h3 class="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
						Вход
					</h3> -->
					<UForm :schema="login_schema" :state="login_state" class="space-y-4" @submit="onLoginSubmit">

						<UFormGroup label="Email" name="email">
							<UInput v-model="login_state.email" />
						</UFormGroup>

						<UFormGroup :label="$t('password')" name="password">
							<UInput v-model="login_state.password" type="password" />
						</UFormGroup>

						<div class="flex justify-end">
							<UCheckbox :label="t('remember_device')" v-model="login_state.remind" />
						</div>

						<div class="flex justify-end pt-5">
							<UButton type="submit" size="lg" :loading="is_loading">
								{{ $t('login') }}
							</UButton>
						</div>

					</UForm>
				</div>
			</template>

			<template #register>
				<div class="p-5">
					<!-- <h3 class="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
						Регистрация
					</h3> -->
					<UForm :schema="register_schema" :state="register_state" class="space-y-4" @submit="onRegisterSubmit" :ref="register_form" :key="should_validate">

						<UFormGroup label="Email" name="email">
							<UInput v-model="register_state.email" />
						</UFormGroup>

						<UFormGroup :label="$t('password')" name="password">
							<UInput v-model="register_state.password" type="password" />
						</UFormGroup>

						<div class="flex justify-end">
							<UButton color="gray" variant="ghost" @click="()=>generate_strong_password()">
								{{ $t('pass_gen') }}
							</UButton>
						</div>

						<div class="flex justify-end pt-5">
							<UButton type="submit" size="lg" :loading="is_loading">
								{{ $t('register') }}
							</UButton>
						</div>

					</UForm>
				</div>
			</template>
		</UTabs>



	</UCard>
</UModal>

</template>

